import { Refine } from '@refinedev/core';
import { ConfigProvider } from 'antd';
import type { ThemeConfig } from 'antd/es/config-provider/context';
import type { AliasToken } from 'antd/es/theme/internal';
import { Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { ThemeProvider } from 'styled-components';

import {
  AppLoading,
  AuthProvider,
  AnalyticsUserIdentifier,
  DataInitializer,
  ErrorBoundary,
  FeatureRoute,
  HomeRedirect,
  LaunchDarklyUserIdentifier,
  Layout,
  LoadingProvider
} from '@/components';
import PersistedQueryParamsGate from '@/components/PersistedQueryParamsGate';
import { notificationProvider } from '@/config';
import { CSS_PREFIX, ROUTES } from '@/constants';
import {
  AppProvider,
  FeatureFlagsProvider,
  PersistedQueryParamsProvider,
  RegionSeasonProvider
} from '@/contexts';
import { AccessGuardian, AuthContainer } from '@/libs';
import { WorkerGuard } from '@/pages/WorkerManagement/components';
import { ComponentsTokens, ThemeTokens } from '@/theme';
import { getDataProviders, lazyWithRetry } from '@/utils';

const AgronomicDemandRouter = lazyWithRetry(
  () => import('@/components/AgronomicDemandRouter')
);
const CropwiseIntegrations = lazyWithRetry(
  () => import('@/pages/CropwiseIntegrations')
);
const CycleFields = lazyWithRetry(() => import('@/pages/CycleFields'));
const DataImport = lazyWithRetry(() => import('@/pages/DataImport'));
const SeasonsManagement = lazyWithRetry(
  () => import('@/pages/SeasonsManagement')
);
const ExportBatchesRouter = lazyWithRetry(
  () => import('@/components/ExportBatchesRouter')
);
const FieldAllocation = lazyWithRetry(() => import('@/pages/FieldAllocation'));
const Grower = lazyWithRetry(() => import('@/pages/Growers/pages/Grower'));
const GrowerList = lazyWithRetry(
  () => import('@/pages/Growers/pages/GrowerList')
);
const GrowerProperty = lazyWithRetry(
  () => import('@/pages/Growers/pages/GrowerProperty')
);
const GrowingSubgrowingAreas = lazyWithRetry(
  () => import('@/pages/GrowingSubgrowingAreas')
);
const NotFoundPage = lazyWithRetry(
  () => import('@/pages/NotFoundPage/NotFoundPage')
);
const ParentSeedAttributes = lazyWithRetry(
  () => import('@/pages/ParentSeedAttributes')
);
const ParentSeedsAssignmentRouter = lazyWithRetry(
  () => import('@/components/ParentSeedsAssignmentRouter')
);
const PendingAccess = lazyWithRetry(
  () => import('@/pages/PendingAccess/PendingAccess')
);
const ProductionInstructions = lazyWithRetry(
  () => import('@/pages/ProductionInstructions/ProductionInstructions')
);
const ProductionInstructionsTemplate = lazyWithRetry(
  () => import('@/pages/ProductionInstructionsTemplate')
);
const ProductionOrderList = lazyWithRetry(
  () => import('@/pages/ProductionOrderManagement/pages/ProductionOrderList')
);
const ReviewSppData = lazyWithRetry(() => import('@/pages/ReviewSppData'));
const SelectCycleFields = lazyWithRetry(
  () => import('@/pages/SelectCycleFields')
);
const VarietyDeviation = lazyWithRetry(
  () => import('@/pages/VarietyDeviation')
);
const WorkerList = lazyWithRetry(
  () => import('@/pages/WorkerManagement/pages/WorkerList/WorkerList')
);

const theme: ThemeConfig = {
  token: ThemeTokens,
  components: ComponentsTokens
};

const dataProviders = getDataProviders();

const App = () => {
  const { token } = theme;

  return (
    <ErrorBoundary>
      <Refine
        dataProvider={dataProviders}
        notificationProvider={notificationProvider}>
        <ConfigProvider theme={theme} prefixCls={CSS_PREFIX}>
          <ThemeProvider theme={token as AliasToken}>
            <BrowserRouter>
              <RegionSeasonProvider>
                <AuthProvider>
                  <AppProvider>
                    <LoadingProvider>
                      <FeatureFlagsProvider>
                        <AnalyticsUserIdentifier />
                        <LaunchDarklyUserIdentifier />
                        <Suspense fallback={<AppLoading />}>
                          <Routes>
                            <Route
                              path={ROUTES.AUTHENTICATE}
                              element={<AuthContainer />}
                            />
                            <Route
                              element={
                                <AccessGuardian>
                                  <DataInitializer />
                                </AccessGuardian>
                              }>
                              <Route index element={<HomeRedirect />} />
                              <Route path={ROUTES.REGION} element={<Layout />}>
                                <Route index element={<NotFoundPage />} />
                                <Route
                                  path={ROUTES.SEASONS_MANAGEMENT}
                                  element={
                                    <PersistedQueryParamsProvider>
                                      <PersistedQueryParamsGate />
                                    </PersistedQueryParamsProvider>
                                  }>
                                  <Route
                                    index
                                    element={<SeasonsManagement />}
                                  />
                                </Route>
                                <Route path='*' element={<NotFoundPage />} />
                                <Route path={ROUTES.SEASON_SCOPE}>
                                  <Route
                                    element={
                                      <PersistedQueryParamsProvider>
                                        <PersistedQueryParamsGate />
                                      </PersistedQueryParamsProvider>
                                    }>
                                    <Route
                                      index
                                      element={<FieldAllocation />}
                                    />

                                    <Route
                                      path={ROUTES.SEASONS_MANAGEMENT}
                                      element={<SeasonsManagement />}
                                    />
                                    <Route
                                      path={ROUTES.GROWER_LIST}
                                      element={<GrowerList />}
                                    />
                                    <Route
                                      path={ROUTES.GROWER}
                                      element={<Grower />}
                                    />
                                    <Route
                                      path={ROUTES.GROWER_PROPERTY}
                                      element={<GrowerProperty />}
                                    />
                                    <Route
                                      path={ROUTES.FIELD_ALLOCATION}
                                      element={<FieldAllocation />}
                                    />
                                    <Route
                                      path={ROUTES.GROWING_SUBGROWING_AREAS}
                                      element={<GrowingSubgrowingAreas />}
                                    />
                                    <Route
                                      path={ROUTES.CYCLE_FIELDS}
                                      element={<CycleFields />}
                                    />
                                    <Route
                                      path={ROUTES.CYCLE_FIELDS_ADD}
                                      element={<SelectCycleFields />}
                                    />
                                    <Route
                                      path={ROUTES.PRODUCTION_ORDER_LIST}
                                      element={<ProductionOrderList />}
                                    />
                                    <Route
                                      path={ROUTES.VARIETY_DEVIATION}
                                      element={<VarietyDeviation />}
                                    />
                                    <Route
                                      path={ROUTES.WORKER_LIST}
                                      element={
                                        <WorkerGuard>
                                          <WorkerList />
                                        </WorkerGuard>
                                      }
                                    />
                                    <Route
                                      path={ROUTES.PARENT_SEED_ATTRIBUTE_LIST}
                                      element={<ParentSeedAttributes />}
                                    />
                                    <Route
                                      path={ROUTES.PARENT_SEEDS_ASSIGNMENT}
                                      element={<ParentSeedsAssignmentRouter />}
                                    />
                                    <Route
                                      path={ROUTES.AGRONOMIC_DEMAND}
                                      element={<AgronomicDemandRouter />}
                                    />
                                    <Route
                                      path={ROUTES.PRODUCTION_INSTRUCTIONS}
                                      element={<ProductionInstructions />}
                                    />
                                    <Route
                                      path={ROUTES.EXPORT_BATCHES}
                                      element={<ExportBatchesRouter />}
                                    />
                                    <Route
                                      path={
                                        ROUTES.PRODUCTION_INSTRUCTIONS_TEMPLATE
                                      }
                                      element={
                                        <FeatureRoute feature='configureWebProductionInstructionsTemplate'>
                                          <ProductionInstructionsTemplate />
                                        </FeatureRoute>
                                      }
                                    />
                                    <Route
                                      path={ROUTES.REVIEW_SPP_DATA}
                                      element={
                                        <FeatureRoute feature='releaseWebReviewSppData'>
                                          <ReviewSppData />
                                        </FeatureRoute>
                                      }
                                    />
                                    <Route
                                      path={ROUTES.DATA_IMPORT}
                                      element={
                                        <FeatureRoute feature='releaseWebDataImport'>
                                          <DataImport />
                                        </FeatureRoute>
                                      }
                                    />
                                    <Route
                                      path={ROUTES.CROPWISE_INTEGRATIONS}
                                      element={
                                        <FeatureRoute feature='configureWebCropwiseIntegrationsScreen'>
                                          <CropwiseIntegrations />
                                        </FeatureRoute>
                                      }
                                    />
                                  </Route>
                                </Route>
                              </Route>
                            </Route>
                            <Route
                              element={
                                <AccessGuardian>
                                  <PendingAccess />
                                </AccessGuardian>
                              }
                              path={ROUTES.PENDING_ACCESS}
                            />
                            <Route path='*' element={<NotFoundPage />} />
                          </Routes>
                        </Suspense>
                      </FeatureFlagsProvider>
                    </LoadingProvider>
                  </AppProvider>
                </AuthProvider>
              </RegionSeasonProvider>
            </BrowserRouter>
          </ThemeProvider>
        </ConfigProvider>
      </Refine>
    </ErrorBoundary>
  );
};

export default App;
